import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrders, fetchOrderById } from '../slices/ordersSlice';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import OrderDetails from '../components/OrderDetails';
import BncLogoSpinner from '../components/BncLogoSpinner';
import { fetchClientById } from '../slices/clientSlice';
import { DollarSign, Search, Calendar, Sliders, ChevronDown, ChevronUp, Eye, RefreshCcw } from 'react-feather';

export default function Billing() {
	const dispatch = useDispatch();
	// const users = useSelector((state) => state?.auth?.users?.items);
	const invoices = useSelector((state) => state.orders.items);
	const listStatus = useSelector((state) => state.orders.listStatus);

	const [clientFirstName, setClientFirstName] = useState('');
	const [clientLastName, setClientLastName] = useState('');
	const [deliveryAddress, setDeliveryAddress] = useState('');
	const [deliveryCity, setDeliveryCity] = useState('');
	const [deliveryState, setDeliveryState] = useState('');
	const [deliveryPostalCode, setDeliveryPostalCode] = useState('');
	const [clientId, setClientId] = useState('');
	const [salesmanId, setSalesmanId] = useState('');
	const [installerId, setInstallerId] = useState('');
	const [status, setStatus] = useState('');
	const [excludeChanged, setExcludeChanged] = useState(false);
	const [includeInvoices, setIncludeInvoices] = useState(true);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [sortColumn, setSortColumn] = useState('createdAt');
	const [sortOrder, setSortOrder] = useState('DESC');
	const [pageSize, setPageSize] = useState(1000);
	const [currentPage, setCurrentPage] = useState(1);

	const [selectedOrder, setSelectedOrder] = useState(null);

	// const [selectDetials, setSelectDetails] = useState(false);
	const order = useSelector((state) => state.orders.order);

	const openDetails = (orderId, clientId) => {
		console.log('orderId', orderId);
		console.log('clientId', clientId);
		setSelectedOrder(orderId);
		dispatch(fetchOrderById(orderId));
		dispatch(fetchClientById(clientId));
	};

	const resetFilters = () => {
		setClientFirstName('');
		setClientLastName('');
		setDeliveryCity('');
		setDeliveryPostalCode('');
		setSalesmanId('');
		setInstallerId('');
		setStartDate('');
		setEndDate('');
		setPageSize(1000);
		setSortColumn('createdAt');
		setSortOrder('DESC');
	};

	useEffect(() => {
		dispatch(
			fetchOrders({
				clientFirstName,
				clientLastName,
				deliveryAddress,
				deliveryCity,
				deliveryState,
				deliveryPostalCode,
				clientId,
				salesmanId,
				installerId,
				status,
				excludeChanged,
				includeInvoices,
				startDate,
				endDate: endDate ? `${endDate}T23:59:59` : endDate,
				sortColumn,
				sortOrder,
				pageSize,
				currentPage,
			})
		);
	}, [
		clientFirstName,
		clientLastName,
		deliveryAddress,
		deliveryCity,
		deliveryState,
		deliveryPostalCode,
		clientId,
		salesmanId,
		installerId,
		status,
		excludeChanged,
		includeInvoices,
		startDate,
		endDate,
		sortColumn,
		sortOrder,
		pageSize,
		currentPage,
		dispatch,
	]);

	const filteredInvoices = invoices.filter(
		(invoice) => invoice.invoices && invoice.invoices.length > 0
	);

	return (
		<div className="flex flex-col h-screen bg-gray-50">
			<Header />
			<div className="flex flex-1 overflow-hidden">
				<Sidebar />
				<main className="flex-1 overflow-auto p-6 md:p-8">
					<div className="max-w-7xl mx-auto space-y-6">
						{/* Page Title */}
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-4">
								<div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20 text-white">
									<DollarSign size={28} />
								</div>
								<div>
									<h1 className="text-2xl font-bold text-gray-900">Billing & Invoices</h1>
									<p className="text-gray-500 text-sm mt-1">Manage payments and invoice history</p>
								</div>
							</div>
						</div>

						{/* Filters Section */}
						<div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
							<div className="flex items-center gap-2 mb-6 text-gray-900 justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 bg-gray-100 rounded-lg">
                                        <Sliders size={18} className="text-gray-600" />
                                    </div>
                                    <h2 className="font-semibold">Filters & Search</h2>
                                </div>
                                <button
                                    onClick={resetFilters}
                                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                    <RefreshCcw size={14} />
                                    Reset Filters
                                </button>
							</div>
							
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
								<div className="space-y-1">
									<label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Client Details</label>
									<input
										type="text"
										placeholder="First Name"
										value={clientFirstName}
										onChange={(e) => setClientFirstName(e.target.value)}
										className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white outline-none transition-all text-sm"
									/>
									<input
										type="text"
										placeholder="Last Name"
										value={clientLastName}
										onChange={(e) => setClientLastName(e.target.value)}
										className="w-full mt-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white outline-none transition-all text-sm"
									/>
								</div>

								<div className="space-y-1">
									<label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location</label>
									<input
										type="text"
										placeholder="City"
										value={deliveryCity}
										onChange={(e) => setDeliveryCity(e.target.value)}
										className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white outline-none transition-all text-sm"
									/>
									<input
										type="text"
										placeholder="Postal Code"
										value={deliveryPostalCode}
										onChange={(e) => setDeliveryPostalCode(e.target.value)}
										className="w-full mt-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white outline-none transition-all text-sm"
									/>
								</div>

								<div className="space-y-1">
									<label className="text-xs font-medium text-gray-500 uppercase tracking-wide">IDs</label>
									<input
										type="text"
										placeholder="Salesman ID"
										value={salesmanId}
										onChange={(e) => setSalesmanId(e.target.value)}
										className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white outline-none transition-all text-sm"
									/>
									<input
										type="text"
										placeholder="Installer ID"
										value={installerId}
										onChange={(e) => setInstallerId(e.target.value)}
										className="w-full mt-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white outline-none transition-all text-sm"
									/>
								</div>

								<div className="space-y-1">
									<label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date Range</label>
									<input
										type="date"
										value={startDate}
										onChange={(e) => setStartDate(e.target.value)}
										className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white outline-none transition-all text-sm text-gray-600"
									/>
									<input
										type="date"
										value={endDate}
										onChange={(e) => setEndDate(e.target.value)}
										className="w-full mt-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white outline-none transition-all text-sm text-gray-600"
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
								<div className="flex items-center gap-3">
									<label className="text-sm text-gray-600 font-medium">Sort By:</label>
									<select
										value={sortColumn}
										onChange={(e) => setSortColumn(e.target.value)}
										className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
									>
										<option value="firstName">First Name</option>
										<option value="lastName">Last Name</option>
										<option value="email">Email</option>
										<option value="createdAt">Created At</option>
									</select>
								</div>
								
								<div className="flex items-center gap-3">
									<label className="text-sm text-gray-600 font-medium">Order:</label>
									<select
										value={sortOrder}
										onChange={(e) => setSortOrder(e.target.value)}
										className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
									>
										<option value="ASC">Ascending</option>
										<option value="DESC">Descending (Newest First)</option>
									</select>
								</div>

								<div className="flex items-center gap-3">
									<label className="text-sm text-gray-600 font-medium">Page Size:</label>
									<input
										type="number"
										value={pageSize}
										onChange={(e) => setPageSize(e.target.value)}
										className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
									/>
								</div>
							</div>
						</div>

						{/* Invoices Table */}
						<div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden relative min-h-[200px]">
                            {listStatus === 'loading' && (
                                <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                                    <BncLogoSpinner size={60} />
                                </div>
                            )}
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-gray-50/50">
										<tr>
											<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
											<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
											<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
											<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</th>
											<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
											<th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">View</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{filteredInvoices.map((invoice) => (
											<tr key={invoice.id} className="hover:bg-gray-50 transition-colors duration-150">
												<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
													#{invoice.invoices[0].invoiceId}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
													{invoice.clientName}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
													${invoice.invoices[0].dueAmount.toFixed(2)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													<div className="flex items-center gap-2">
														<Calendar size={14} />
														{new Date(invoice.invoices[0].paymentDate).toLocaleDateString('en-US')}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
														${invoice.invoices[0].status === 2 ? 'bg-green-100 text-green-800' : 
														  invoice.invoices[0].status === 1 ? 'bg-yellow-100 text-yellow-800' : 
														  'bg-red-100 text-red-800'}`}>
														{invoice.invoices[0].status === 1
															? 'Partially paid'
															: invoice.invoices[0].status === 2
															? 'Paid'
															: 'Unpaid'}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<button
														onClick={() => openDetails(invoice.id, invoice.clientId)}
														className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
														title="View Order Details"
													>
														<Eye size={18} />
													</button>
												</td>
											</tr>
										))}
										{filteredInvoices.length === 0 && (
											<tr>
												<td colSpan="5" className="px-6 py-12 text-center text-gray-500 text-sm">
													No invoices found matching your criteria
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</main>
			</div>
			{selectedOrder && (
				<OrderDetails
					order={order} // Pass the fetched order from Redux
					onClose={() => setSelectedOrder(null)}
				/>
			)}
		</div>
	);
}
